import {JourneyStepData} from "@/domains/-journey-steps/types.ts";
import JourneyStep from "@/domains/-journey/models/JourneyStep.ts";
import {PersonRoleCoachee} from "@/domains/-workspace/models/PersonRoleCoachee";
import {dateFromServer} from "@/functions/dates.ts";
import sdk, {
  GetJourneyMeeting,
  GetJourneyStepProps,
  GetPersonJourney,
  JourneyId,
  JourneyName,
  JourneySchema,
  JourneyStepGroup,
  JourneyStepId,
  JourneyStepName,
  JourneyStepType,
  PersonId,
  Version,
  WorkspaceId
} from "@/sdk";


function isMissingExternError(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'response' in error
    && typeof error.response === 'object'
    && error.response !== null
    && 'data' in error.response
    && typeof error.response.data === 'object'
    && error.response.data !== null
    && 'detail' in error.response.data
    && error.response.data.detail === 'journey.no_extern';
}


export default class Journey {
  readonly public_id: JourneyId;
  readonly name: JourneyName;
  readonly person_id: PersonId;
  readonly workspace_id: WorkspaceId;
  readonly created_at: Date;
  readonly version: Version;
  readonly steps: {
    [key in JourneyStepId]?: JourneyStep;
  }
  readonly alias: string | undefined;
  readonly schema: JourneySchema;
  readonly coachee: PersonRoleCoachee;
  readonly extern_pid: undefined | PersonId;
  meetingConfiguration: undefined | GetJourneyMeeting.Result;
  updated_at: Date | null;
  is_closed: boolean;


  constructor(workspaceId: WorkspaceId, props: GetPersonJourney.Result, coacheeRole: PersonRoleCoachee) {
    this.public_id = props.public_id;
    this.workspace_id = workspaceId;
    this.name = props.name;
    this.created_at = dateFromServer(props.created_at);
    this.updated_at = props.updated_at ? dateFromServer(props.updated_at) : null;
    this.is_closed = props.is_closed;
    this.steps = {};
    this.schema = props.journey_schema;
    this.version = props.journey_schema.version;
    this.coachee = coacheeRole
    this.extern_pid = props.extern_pid

    Object
      .entries(props.journey_schema.steps)
      .forEach(([stepId, stepMeta]) => {

        const stepData: GetJourneyStepProps | undefined = props.steps.find(stepData => stepData.name === stepId);
        this.steps[stepId] = new JourneyStep(this, stepId as JourneyStepId, stepMeta, stepData);
      })

    
  }

  async loadMeetingInfo(){
    if(this.name === 'feta_meeting'){
      await sdk.getJourneyMeeting(this.public_id, 'feta_action_plan_meeting').then(response => {
        this.meetingConfiguration = response.data
      })
    }
  }

  hasStepById(stepName: JourneyStepId): boolean {
    return Object.hasOwn(this.steps, stepName)
  }

  getStepById(stepName: JourneyStepId): JourneyStep | null {
    return this.hasStepById(stepName) ? this.steps[stepName] as JourneyStep : null
  }

  getStepByName<Name extends JourneyStepName>(stepName: Name): undefined | JourneyStep<JourneyStepData[Name]> {
    return Object.values(this.steps).find(step => step.name === stepName) as undefined | JourneyStep<JourneyStepData[Name]>;
  }

  getStep(props: {
    id?: JourneyStepId;
    name?: JourneyStepName;
    type?: JourneyStepType;
    group?: JourneyStepGroup;
  }): JourneyStep | null {
    let steps = Object.values(this.steps);

    if (props.id)
      steps = steps.filter(step => step.id === props.id)
    if (props.group)
      steps = steps.filter(step => step.group === props.group)
    if (props.type)
      steps = steps.filter(step => step.type === props.type)
    if (props.name)
      steps = steps.filter(step => step.name === props.name)

    if (steps.length === 0) return null

    return steps[0]
  }

  get experienceReportSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'experience')
      .filter(step => step.type === 'report')
  }

  get impactReportSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'impact')
      .filter(step => step.type === 'report')
  }

  get personSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'experience')
      .filter(step => step.type === 'survey' || step.type === 'game')
  }

  get impactPersonSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'impact')
      .filter(step => step.type === 'survey' || step.type === 'game')
  }

  get ignitionSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group !== 'impact')
      .filter(step => step.type === 'survey' || step.type === 'game' || step.type === 'report' || step.id === 'feta_behaviors_awareness')
  }

  get allSteps(): JourneyStep[] {
    return Object.values(this.steps)
  }

  get allMandatorySteps(): JourneyStep[] {
    const optionalSteps: JourneyStepId[] = ['feta_elearning', 'feta_ascent_offboarding', 'hdf_upload', 'synthesis_trigger', 'edit_synthesis', 'tutorial', 'phenomenology', 'my_omind_invitation','chrysoberyl_synthesis', 'tourmaline_synthesis', 'sapphire_synthesis']
    return Object.values(this.steps)
      .filter(step => step.type !== 'api_action' && step.type !== 'ai_debrief'  && step.type !== 'ai' && step.type !== 'trigger')
      .filter(step => !optionalSteps.includes(step.id))
  }

  get personStepsTotal(): number {
    return this.personSteps.length
  }

  get personStepsDone(): number {
    return this.personSteps.filter(step => step.is_finished).length
  }

  get allPersonStepsFinished(): boolean {
    return this.personSteps.every(step => step.is_finished)
  }

  get labelKey(): string {
    if (this.name == 'feta') {
      if (this.getStep({id: 'feta_ascent_threshold'})?.is_finished)
        return 'feta_ascent'
      else if (this.getStep({id: 'feta_launch_threshold'})?.is_finished)
        return 'feta_launch'
      else if (this.getStep({id: 'feta_action_plan_threshold'})?.is_finished)
        return 'feta_ascent'
      else
        return 'feta_ignition'

    }
    if (this.name == 'feta_meeting') {
      if (this.getStep({id: 'feta_action_plan_threshold'})?.is_finished)
        return 'feta_meeting_level_2'
      else
        return 'feta_meeting_level_1'

    }
    return this.name
  }

  get labelKeyShort(): string {
    if (this.name == 'feta') {
      if (this.getStep({id: 'feta_ascent_threshold'})?.is_finished)
        return 'feta_ascent_short'
      else if (this.getStep({id: 'feta_launch_threshold'})?.is_finished)
        return 'feta_launch_short'
      else if (this.getStep({id: 'feta_action_plan_threshold'})?.is_finished)
        return 'feta_ascent_short'
      else
        return 'feta_ignition_short'

    }
    if (this.name == 'feta_meeting') {
      if (this.getStep({id: 'feta_action_plan_threshold'})?.is_finished)
        return 'feta_meeting_level_2_short'
      else
        return 'feta_meeting_level_1_short'

    }
    return this.name+'_short'
  }

  get isAborted(): boolean {
    if(this.hasStepById('feta_ascent_threshold')){
      if(this.getStep({id: 'feta_ascent_threshold'})?.is_finished){
        if(this.is_closed && !this.allMandatorySteps.every(step => step.is_finished))
          return true
      }
      else{
        if(this.is_closed && !this.ignitionSteps.every(step => step.is_finished))
          return true
      }
    }
    else{
      if(this.is_closed && !this.allMandatorySteps.every(step => step.is_finished))
        return true
    }
    
    return false
  }

  get canBeDeleted(): boolean {
    return this.schema.deletion_guards.every(stepId => {
      const step = this.getStepById(stepId);

      if (!step) return true;

      return !step.started_at;
    })
  }

  get hasMeetingPossibility(): boolean{
    return !!this.getStepById('feta_launch_threshold')?.is_finished 
  }

  get hasMeetingConfigured(): boolean{
    return !!this.meetingConfiguration
  }
}
